import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { beginLoading, endLoading, cancelTimeout } from "./social_sign_in_loading"

// Bridge pair: bridge--google-sign-in ↔ GoogleSignInComponent (iOS).
//
// Flow:
//   1. User taps the Google button in the webview.
//   2. We immediately swap the button into a loading state and disable
//      peer auth controls (other provider, password form).
//   3. We send a "signIn" message to native; iOS presents the Google
//      sheet, gets an ID token, and replies with { idToken } (or { error }).
//   4. On success we POST the token to /:locale/auth/google via a hidden
//      form so Turbo handles the redirect + session cookie set. The
//      loading state stays on until navigation; if the POST fails we
//      restore the button via turbo:submit-end.
//   5. On native error or user cancel, we restore the button. Cancel is
//      restored silently; everything else surfaces a toast.
//
// Browser (no native bridge): this controller's `enabled` is false and the
// click is a no-op. This is intentional — FloosYo is mobile-only in
// production; test Google sign-in via the iOS simulator against the
// Cloudflare tunnel.
export default class extends BridgeComponent {
  static component = "google-sign-in"
  static values = {
    endpoint: String,
    csrfToken: String,
    loadingLabel: String,
    errorMessage: String,
    canceledMessage: String
  }

  signIn(event) {
    event?.preventDefault()

    if (!this.enabled) {
      console.warn("[google-sign-in] native bridge not enabled — ignoring click")
      return
    }
    if (this._signInLoading) return

    beginLoading(this, { label: this.loadingLabelValue || "Signing you in…" })

    this.send("signIn", {}, (message) => {
      const data = message?.data || {}

      if (data.error) {
        const isCancel = isCancelError(data.error)
        endLoading(this, {
          error: !isCancel,
          errorMessage: isCancel ? null : (this.errorMessageValue || "Sign-in failed. Please try again.")
        })
        if (!isCancel) {
          console.warn("[google-sign-in] native returned error:", data.error)
        }
        return
      }

      if (!data.idToken) {
        endLoading(this, {
          error: true,
          errorMessage: this.errorMessageValue || "Sign-in failed. Please try again."
        })
        console.warn("[google-sign-in] missing idToken in reply")
        return
      }

      this.submitToken(data.idToken)
    })
  }

  submitToken(idToken) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.endpointValue
    form.hidden = true
    form.appendChild(this.hiddenField("authenticity_token", this.csrfTokenValue))
    form.appendChild(this.hiddenField("id_token", idToken))

    // Onboarding hand-off: when this button is on the auth step, the
    // anonymous-flow answers live on window.__onboardingPayload. Forward
    // them so the server provisions the user in one transaction.
    const onboardingPayload = (typeof window !== "undefined") ? window.__onboardingPayload : null
    if (onboardingPayload) {
      form.appendChild(this.hiddenField("onboarding_payload", JSON.stringify(onboardingPayload)))
    }

    // If Turbo can't complete the form submission (network error, server
    // 5xx without a redirect), restore the button so the user can retry.
    form.addEventListener("turbo:submit-end", (event) => {
      if (event.detail?.success) {
        cancelTimeout(this)
      } else {
        endLoading(this, {
          error: true,
          errorMessage: this.errorMessageValue || "Sign-in failed. Please try again."
        })
      }
    }, { once: true })

    document.body.appendChild(form)
    form.requestSubmit()
  }

  disconnect() {
    cancelTimeout(this)
    super.disconnect?.()
  }

  hiddenField(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }
}

function isCancelError(message) {
  // Google iOS SDK reports user cancel as a localized "canceled" string. Be
  // permissive — match common variants we've actually observed in logs.
  if (!message) return false
  return /cancell?ed|user.?cancel/i.test(message)
}
